import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Row, Col } from 'antd';

import {
  getTransactionAccessSelector,
  getTransactionSelector,
  getTranslationAdminAccessSelector,
  getKeyDatesSelector,
  getTransactionAgentsCoordinatorsSelector,
  getTransactionDetailsSelector,
} from 'store/selectors/transaction';
import { getRelatedAgentCoordinatorContactsFormattedList } from 'store/selectors/relatedContacts';
import { LeftSide, RightSide } from 'components/Transactions';
import { Button } from 'components-antd';
import { DetailsView } from 'pages/Workshop/Transactions/components/DetailsView';
import {
  PropertyTitle,
  Price,
  ClosingDate,
  Status,
  Timeline,
  NeedsAttention,
  NamesArray,
} from '..';
import { listedStatusesArray, transactionStatuses } from 'settings/constants/transaction';
import { getUserRoleSelector } from 'store/selectors/user';
import { AGENT, CLIENT, transactionRoles } from 'settings/constants/roles';

import styles from './styles.module.scss';
import ProjectTitle from '../ProjectTitle';
import useIsProjectRoute from 'hooks/use-is-project-route';
import { isAfterToday, isToday } from 'helpers';
import { getTransactionEndDate, getTransactionPrice } from 'utils';
import { CancelledDetails } from '../CancelledDetails';

import { getTransactionAgentsCoordinators } from 'store/effects/transactions';
import {
  requestGetAgentCoordinatorRelatedContactsEffect,
  requestGetTransactionRelatedContactsEffect,
} from 'store/effects/relatedContacts';
import {
  TransactionRoomWrapper,
  VARIANTS,
} from 'pages/Workshop/Transactions/components/TransactionRoomWrapper';
import { LinkOut } from 'components/Icons';

const MoreDetailsButton = ({ onClick }) => (
  <Button variant="hollow-bordered" className={styles.moreDetailsBtn} onClick={onClick}>
    More Details <LinkOut />
  </Button>
);

const TransactionView = (props) => {
  const { className } = props;
  const dispatch = useDispatch();
  const userRole = useSelector(getUserRoleSelector);
  const { transaction, address } = useSelector(getTransactionSelector);
  const { fullAccess } = useSelector(getTransactionAccessSelector);
  const rightSideRef = useRef();
  const { isTransactionAdminOrOwner } = useSelector(getTranslationAdminAccessSelector);
  const keyDates = useSelector(getKeyDatesSelector);
  const agentsCoordinators = useSelector(getTransactionAgentsCoordinatorsSelector);
  const agentCoordinatorsMembers = useSelector(getRelatedAgentCoordinatorContactsFormattedList);
  const isProject = useIsProjectRoute();
  const [editDetailId, setEditDetailId] = useState();
  const [editId, setEditId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const isListed = listedStatusesArray.includes(transaction.Status);
  const isClosed = transaction.Status === transactionStatuses.Closed;
  const isCanceled = transaction.Status === transactionStatuses.Canceled;
  const keyDatesPrices = transaction?.PropertyTransactionKeyDatesPrices;
  const details = useSelector(getTransactionDetailsSelector);

  const formattedAgentCoordinatorsMembers = useMemo(
    () =>
      agentCoordinatorsMembers?.map((item) => ({
        ...item,
        Id: item?.UserId ? `${item?.UserId}-user` : `${item?.ContactId}-contact`,
      })),
    [agentCoordinatorsMembers],
  );

  // Props that disables other fields when one is opened for modification
  const isDisableField = (editKey) => editId !== null && editId !== editKey;
  const editProps = { setEditId, isDisableField };
  const metaDataColProps = {
    className: styles.colData,
    xs: 24,
    lg: 12,
  };

  const renderProjectSection = ({ ...editProps }) => {
    const getCorrespondingDate = () => {
      const { Status } = transaction;
      return Status === transactionStatuses.Closed && keyDates?.CloseDate
        ? keyDates?.CloseDate
        : keyDates?.KickoffDate &&
          (isToday(keyDates?.KickoffDate) || isAfterToday(keyDates?.KickoffDate))
        ? keyDates?.KickoffDate
        : keyDates?.CompletionDate || keyDates?.ExpiredDate;
    };

    const getCorrespondingLabel = () => {
      const label = isClosed ? 'Closed' : '';

      return (
        label ||
        (keyDates?.KickoffDate &&
        (isToday(keyDates?.KickoffDate) || isAfterToday(keyDates?.KickoffDate))
          ? 'Kickoff'
          : 'Deadline')
      );
    };

    const getItemToUpdateKey = () => {
      return isClosed
        ? 'CloseDate'
        : keyDates?.KickoffDate &&
          (isToday(keyDates?.KickoffDate) || isAfterToday(keyDates?.KickoffDate))
        ? 'KickoffDate'
        : 'CompletionDate';
    };

    return (
      <TransactionRoomWrapper
        variant={VARIANTS.CONFETTI_BLUE}
        className={styles.transactionInfoContainer}
      >
        <Row className={styles.addressStatusRow} align="middle">
          <Col xs={24} lg={14}>
            <ProjectTitle {...editProps} />
          </Col>
          <Col xs={24} lg={10}>
            <Status editId={editId} />
          </Col>
        </Row>
        <Row>
          <Col {...metaDataColProps}>
            <ClosingDate
              label={getCorrespondingLabel()}
              value={getCorrespondingDate()}
              itemToUpdateKey={getItemToUpdateKey()}
              {...editProps}
            />
            {details?.length !== 0 && (
              <MoreDetailsButton onClick={() => setShowDetailsModal(true)} />
            )}
          </Col>
          <Col {...metaDataColProps}>
            <NamesArray
              keyToUpdate="agentsOrOwners"
              label="Owner(s)"
              namesArray={transaction?.AgentsOrOwners}
              stringArrayNames
              {...editProps}
            />
          </Col>
        </Row>
      </TransactionRoomWrapper>
    );
  };

  const getDateLabel = () => {
    if (isListed) return 'Expires';
    else if (isClosed) return 'Closed';
    else if (isCanceled) return 'Canceled';
    else return 'Closing';
  };

  const getDateItemToUpdate = () => {
    if (isListed) return 'ListingExpireDate';
    else if (isClosed) return 'CloseDate';
    else if (isCanceled) return 'CanceledDate';
    else return 'UnderContractCloseDate';
  };

  const fetchAgentsCoordinators = () => {
    dispatch(getTransactionAgentsCoordinators(transaction?.Id));

    // Fetching related contacts
    dispatch(requestGetAgentCoordinatorRelatedContactsEffect({ includeAgents: true }));
    // Fetching related contacts specific to transaction
    dispatch(requestGetTransactionRelatedContactsEffect({ transactionId: transaction?.Id }));
  };

  const roleInfoProp = useMemo(() => {
    const { RepresentingRoles } = transaction || {};

    if (!RepresentingRoles) return {};

    const isBuyer = RepresentingRoles.includes(transactionRoles.BUYER);
    const isSeller = RepresentingRoles.includes(transactionRoles.SELLER);

    if (isBuyer && isSeller) {
      return { isAgentInfo: true };
    }

    return isBuyer ? { isBuyerTransaction: true } : { isSellerTransaction: true };
  }, [transaction]);

  const renderTransactionSection = ({ ...editProps }) => {
    return (
      <TransactionRoomWrapper
        variant={VARIANTS.CONFETTI_BLUE}
        className={styles.transactionInfoContainer}
      >
        <Row className={styles.addressStatusRow} align="middle">
          <Col xs={24} lg={14}>
            <PropertyTitle {...editProps} />
          </Col>
          <Col xs={24} lg={10}>
            <Status editId={editId} />
          </Col>
        </Row>
        <Row gutter={20}>
          <Col {...metaDataColProps}>
            <Price
              className={styles.value}
              value={getTransactionPrice(transaction.Status, keyDatesPrices)}
              {...editProps}
            />
            <ClosingDate
              label={getDateLabel()}
              value={getTransactionEndDate(transaction.Status, keyDatesPrices, isListed)}
              itemToUpdateKey={getDateItemToUpdate()}
              {...editProps}
            />
            <NamesArray
              keyToUpdate="transactionClients"
              label="Client(s)"
              namesArray={transaction?.TransactionClients}
              stringArrayNames
              {...editProps}
            />
            <MoreDetailsButton onClick={() => setShowDetailsModal(true)} />
          </Col>
          <Col {...metaDataColProps}>
            <NamesArray
              keyToUpdate="agentsOrOwners"
              label="Agent(s)"
              namesArray={
                agentsCoordinators?.Agents?.map((item) => ({
                  label: item?.Name,
                  value: item?.Id,
                  participantId: item?.ParticipantId,
                  isParticipant: item?.IsParticipant,
                })) || []
              }
              memberContacts={formattedAgentCoordinatorsMembers || []}
              refetchAgentsCoordinators={fetchAgentsCoordinators}
              {...roleInfoProp}
              {...editProps}
            />
            <NamesArray
              keyToUpdate="coordinators"
              label="Coordinator(s)"
              namesArray={
                agentsCoordinators?.Coordinators?.map((item) => ({
                  label: item?.Name,
                  value: item?.Id,
                  participantId: item?.ParticipantId,
                  isParticipant: item?.IsParticipant,
                })) || []
              }
              memberContacts={formattedAgentCoordinatorsMembers || []}
              refetchAgentsCoordinators={fetchAgentsCoordinators}
              isCoordinatorInfo
              {...editProps}
            />
          </Col>
        </Row>
      </TransactionRoomWrapper>
    );
  };
  return (
    <div testid="transaction_overview" className={classNames(styles.transactionView, className)}>
      <LeftSide>
        <p className={styles.titleOverview}>Overview</p>
        <div className={styles.topSideInfo}>
          {isProject
            ? renderProjectSection({ ...editProps })
            : renderTransactionSection({ ...editProps })}
          {isCanceled ? (
            <CancelledDetails
              ReasonForCancellation={transaction?.ReasonForCancellation}
              AdditionalCancellationComments={transaction?.AdditionalCancellationComments}
            />
          ) : (
            <NeedsAttention
              transactionId={transaction?.Id}
              showFullAccessContent={
                !isProject && userRole === AGENT && (fullAccess || isTransactionAdminOrOwner)
              }
              showClientContent={userRole === CLIENT}
            />
          )}
        </div>
        <DetailsView
          editDetailId={editDetailId}
          setEditDetailId={setEditDetailId}
          isProject={isProject}
          showDetailsModal={showDetailsModal}
          setShowDetailsModal={setShowDetailsModal}
          secondaryText={
            isProject
              ? transaction?.Name
              : `${address?.Line1} ${address?.Line2 ? `#${address?.Line2}` : ''}`
          }
        />
      </LeftSide>
      <RightSide ref={rightSideRef}>
        <Timeline rightSideRef={rightSideRef} isProject={isProject} />
      </RightSide>
    </div>
  );
};

TransactionView.propTypes = {
  className: PropTypes.string,
};

TransactionView.defaultProps = {
  className: '',
};

export default TransactionView;
